import { useState, useEffect, useRef } from 'react'
import { useForecast } from '../../hooks/useForecast'
import { C, F } from './theme'
import { Card, SectionHead, Pill, Btn, Icon, Head } from './prims'
import { ApiClient } from '../../api'
import type { Insight } from '../../types'

// SCREEN EM-5 — Market research (Claude + hosted web search). A structured
// brief (seeded from the concept overview) on the left with a Claude chat to
// shape it; live sector research on the right returning accepted-into-forecast
// insights. Money never appears here, so no financial gating is needed.

type ChatMsg = { role: 'user' | 'assistant'; content: string }

const STARTERS = [
  'What price ceiling can we test on the peak day?',
  'How do comparable lines structure their upsells?',
  'What are the licensing pitfalls to check first?'
]

const SOURCES = [
  'HRA visitor report',
  'Comparable-line pricing',
  'School-holiday calendar',
  'Competitor calendar',
  'Met Office climatology'
]

const CHAT_FALLBACK =
  'The live assistant is unavailable just now — your brief edits are still saved. Try again in a moment, or refine the brief fields directly.'

// Deep-equal for the accepted-state comparison. Insights are small, flat
// objects so a field-by-field walk is enough (and avoids JSON key-order traps).
function sameInsights(a: Insight[], b: Insight[]): boolean {
  if (a.length !== b.length) return false
  return a.every((x, i) => {
    const y = b[i]
    return !!y && x.icon === y.icon && x.tag === y.tag && x.color === y.color && x.head === y.head && x.body === y.body
  })
}

export function ResearchTab({ eventId }: { eventId: number }) {
  const { fc, save } = useForecast(eventId)

  // Brief — local mirror of fc.brief, saved on blur.
  const [sector, setSector] = useState('')
  const [comps, setComps] = useState('')
  const [questions, setQuestions] = useState('')

  // Chat.
  const [chat, setChat] = useState<ChatMsg[]>([])
  const [input, setInput] = useState('')
  const [chatBusy, setChatBusy] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Research run.
  const [running, setRunning] = useState(false)
  const [degraded, setDegraded] = useState(false)
  const [insights, setInsights] = useState<Insight[]>([])
  const [summary, setSummary] = useState('')
  const [saving, setSaving] = useState(false)

  // Seed local state ONCE per idea — not on every fc mutation, or an in-flight
  // save's echo would clobber the field the planner is currently typing in.
  const seededRef = useRef<number | null>(null)
  useEffect(() => {
    if (!fc) return
    if (seededRef.current === eventId) return
    seededRef.current = eventId
    setSector(fc.brief.sector ?? '')
    setComps(fc.brief.comps ?? '')
    setQuestions(fc.brief.questions ?? '')
    setInsights(fc.insights ?? [])
    setSummary(fc.researchSummary ?? '')
  }, [fc, eventId])

  // Keep the transcript pinned to the newest message / thinking indicator.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [chat, chatBusy])

  if (!fc) return null

  const brief = { sector, comps, questions }
  const saveBrief = () => { void save({ brief }).catch(() => {}) }

  const send = async (raw?: string) => {
    const text = (raw ?? input).trim()
    if (!text || chatBusy) return
    const next: ChatMsg[] = [...chat, { role: 'user', content: text }]
    setChat(next)
    setInput('')
    setChatBusy(true)
    try {
      const res = await ApiClient.researchChat(eventId, next, brief)
      const reply = res.degraded ? CHAT_FALLBACK : (res.reply || CHAT_FALLBACK)
      setChat([...next, { role: 'assistant', content: reply }])
    } catch {
      setChat([...next, { role: 'assistant', content: CHAT_FALLBACK }])
    } finally {
      setChatBusy(false)
    }
  }

  const runResearch = async () => {
    if (running) return
    setRunning(true)
    setDegraded(false)
    try {
      const res = await ApiClient.researchRun(eventId, brief)
      setInsights(res.insights ?? [])
      setSummary(res.summary ?? '')
      if (res.degraded) setDegraded(true)
    } catch {
      setDegraded(true)
    } finally {
      setRunning(false)
    }
  }

  const accept = async () => {
    if (saving) return
    setSaving(true)
    try { await save({ insights, researchSummary: summary }) } catch { /* never throw */ } finally { setSaving(false) }
  }

  const hasInsights = insights.length > 0
  const accepted = hasInsights && sameInsights(insights, fc.insights ?? []) && summary === (fc.researchSummary ?? '')

  // ── Reusable brief textarea ────────────────────────────────────────────────
  const briefField = (
    label: string,
    value: string,
    setValue: (v: string) => void,
    rows: number
  ) => (
    <div style={{ marginBottom: 11 }}>
      <div style={{ fontFamily: F.body, fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: C.inkFaint, marginBottom: 4 }}>{label}</div>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={saveBrief}
        rows={rows}
        style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${C.lineSoft}`, borderRadius: 8, padding: '7px 10px', fontFamily: F.body, fontSize: 13, fontWeight: 600, color: C.ink, background: C.paper, outline: 'none', resize: 'vertical', lineHeight: 1.4 }}
      />
    </div>
  )

  return (
    <div style={{ maxWidth: 1120 }}>
      <style>{`@keyframes fcpulse{0%,100%{opacity:1}50%{opacity:.45}}@keyframes fcspin{to{transform:rotate(360deg)}}`}</style>

      <Head
        eyebrow="Market research by Claude"
        title="Research brief & insights"
        sub="A structured brief — extracted from the concept overview — then Claude runs contextual sector research and returns core insights and ideas."
        right={<Pill fg={C.green} bg={C.greenSoft} icon="sparkle">Powered by Claude</Pill>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))', gap: 16, alignItems: 'start' }}>

        {/* LEFT — brief + chat */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card>
            <SectionHead icon="file" label="Research brief" right={<Pill fg={C.brass} bg={C.brassSoft}>auto-extracted</Pill>} />
            {briefField('Sector / format', sector, setSector, 1)}
            {briefField('Comparators', comps, setComps, 1)}
            {briefField('Key questions', questions, setQuestions, 2)}
            <div style={{ fontFamily: F.body, fontSize: 12, color: C.inkFaint, lineHeight: 1.5, marginTop: 2 }}>
              The brief is seeded from the event overview and type — Claude reads it as the research context. Edits here update what gets sent with each question.
            </div>
          </Card>

          <Card>
            <SectionHead icon="sparkle" label="Shape the brief with Claude" />
            <div ref={scrollRef} style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 230, overflowY: 'auto', paddingRight: 4, marginBottom: 10 }}>
              {chat.length === 0 && !chatBusy ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft, marginBottom: 2 }}>Ask Claude to help refine the brief — try:</div>
                  {STARTERS.map((st) => (
                    <button
                      key={st}
                      type="button"
                      onClick={() => void send(st)}
                      style={{ textAlign: 'left', padding: '8px 11px', borderRadius: 9, border: `1px solid ${C.lineSoft}`, background: C.paper, cursor: 'pointer', fontFamily: F.body, fontSize: 12.5, color: C.ink }}
                    >
                      {st}
                    </button>
                  ))}
                </div>
              ) : null}

              {chat.map((m, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '86%', padding: '9px 12px', borderRadius: 13, fontFamily: F.body, fontSize: 12.5, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                    ...(m.role === 'user'
                      ? { background: C.greenBg, color: C.green, borderBottomRightRadius: 4, border: `1px solid ${C.greenSoft}` }
                      : { background: C.card, color: C.ink, border: `1px solid ${C.lineSoft}`, borderBottomLeftRadius: 4 })
                  }}>
                    {m.content}
                  </div>
                </div>
              ))}

              {chatBusy ? (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <div style={{ padding: '9px 12px', borderRadius: 13, background: C.card, border: `1px solid ${C.lineSoft}`, fontFamily: F.mono, fontSize: 12, color: C.inkSoft, animation: 'fcpulse 1.3s infinite' }}>
                    Claude is thinking…
                  </div>
                </div>
              ) : null}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); void send() }} style={{ display: 'flex', gap: 8 }}>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Refine the research brief…"
                disabled={chatBusy}
                style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', border: `1px solid ${C.line}`, borderRadius: 9, padding: '9px 11px', fontFamily: F.body, fontSize: 13, color: C.ink, outline: 'none', background: C.card }}
              />
              <Btn type="submit" variant="primary" icon="send" disabled={chatBusy || !input.trim()}>Send</Btn>
            </form>
          </Card>
        </div>

        {/* RIGHT — insights */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {!hasInsights ? (
            <Card>
              <div style={{ textAlign: 'center', padding: '22px 10px' }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: C.greenBg, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <Icon name="globe" size={26} color={C.green} />
                </div>
                <div style={{ fontFamily: F.serif, fontSize: 18, fontWeight: 700, color: C.ink }}>Run contextual market research</div>
                <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkSoft, margin: '8px auto 16px', maxWidth: 320, lineHeight: 1.55 }}>
                  Live sector research grounded in web search — pricing, licensing, demand, upsell, weather risk.
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  {running ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontFamily: F.body, fontSize: 13.5, fontWeight: 800, color: C.green }}>
                      <span style={{ width: 16, height: 16, border: `2.5px solid ${C.greenSoft}`, borderTopColor: C.green, borderRadius: '50%', animation: 'fcspin .8s linear infinite' }} />
                      Researching the sector…
                    </span>
                  ) : (
                    <Btn variant="primary" icon="globe" onClick={() => void runResearch()}>Run market research</Btn>
                  )}
                </div>
                <div style={{ marginTop: 16, display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {SOURCES.map((src) => <Pill key={src} fg={C.inkSoft} bg={C.paper}>{src}</Pill>)}
                </div>
              </div>
              {degraded ? (
                <div style={{ fontFamily: F.body, fontSize: 12, color: C.oxblood, lineHeight: 1.5, marginTop: 4, textAlign: 'center' }}>
                  Live research unavailable — retry or add findings manually.
                </div>
              ) : null}
            </Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Pill fg={C.green} bg={C.greenSoft} icon="checkc">Research complete</Pill>
                <span style={{ fontFamily: F.body, fontSize: 12, color: C.inkFaint }}>Scanned {SOURCES.length} sources</span>
                <div style={{ marginLeft: 'auto' }}>
                  <Btn variant="ghost" small icon="refresh" onClick={() => void runResearch()} disabled={running}>
                    {running ? 'Researching…' : 'Re-run'}
                  </Btn>
                </div>
              </div>

              {degraded ? (
                <div style={{ fontFamily: F.body, fontSize: 12, color: C.oxblood, lineHeight: 1.5 }}>
                  Live research unavailable — retry or add findings manually.
                </div>
              ) : null}

              {summary ? (
                <Card style={{ borderLeft: `3px solid ${C.green}`, marginBottom: 2 }}>
                  <SectionHead icon="file" label="Research summary" />
                  <div style={{ fontFamily: F.body, fontSize: 13, color: C.ink, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{summary}</div>
                </Card>
              ) : null}

              {insights.map((ins, i) => (
                <Card key={i} pad={0} style={{ borderLeft: `3px solid ${ins.color}`, marginBottom: 10 }}>
                  <div style={{ padding: '13px 15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                      <Icon name={ins.icon} size={15} color={ins.color} />
                      <span style={{ fontFamily: F.body, fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: ins.color }}>{ins.tag}</span>
                      <span style={{ fontFamily: F.serif, fontSize: 15, fontWeight: 700, color: C.ink }}>{ins.head}</span>
                    </div>
                    <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft, lineHeight: 1.55 }}>{ins.body}</div>
                  </div>
                </Card>
              ))}

              <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                <Btn variant="soft" icon="check" onClick={() => void accept()} disabled={saving || accepted}>
                  {accepted ? 'Accepted into forecast' : saving ? 'Accepting…' : 'Accept into forecast'}
                </Btn>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
